import { apiUrl } from "../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import { GridActionsCellItem } from '@mui/x-data-grid';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import DataGridTable from '../Components/DataGridTable';
import Spinner from '../Components/Spinner';
import AppDialog from '../Components/AppDialog';

import { useAuth } from '../hooks/AuthProvider';
import { deleteAutomation, deleteMultiAutomation, setAutomationEnabled } from '../lib/actions';
import { getErrorMessage } from '../utils/errorUtils';
import { LanguageContext } from '../context/LanguageContext';
import automationsTranslations from '../languages/automation.json';
import { AutomationHeaderIcon } from '../svg/AutomationIcons';
import AutomationBuilderPage from '../Components/automation/AutomationBuilderPage';
import AutomationTransferOrganizationModal from '../Components/automation/AutomationTransferOrganizationModal';
import './automations.css';
import './edit.css';
import './details.css';
import { DeleteIcon, DisableIcon, EnableIcon } from '../svg/UtilsIcons';

const createDialogState = () => ({
  open: false,
  type: 'alert',
  variant: 'info',
  icon: 'alert',
  title: '',
  message: '',
  confirmText: 'Ok',
  cancelText: 'Cancel',
  showCancel: false,
});

export const Automations = () => {
  const navigate = useNavigate();
  const { org, permissions } = useAuth();
  const { lang } = useContext(LanguageContext);

  const t = automationsTranslations[lang];
  const isRtl = ['ar', 'ur'].includes(lang);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'desc' }]);
  const [refresh, setRefresh] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [transfer, setTransfer] = useState({ open: false, id: 0 });
  const [dialog, setDialog] = useState(createDialogState());
  const resolverRef = useRef(null);

  const closeDialog = useCallback((result = false) => {
    setDialog((current) => ({ ...current, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (options) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: options?.type || 'alert',
          variant: options?.variant || 'info',
          icon: options?.icon || 'alert',
          title: options?.title || '',
          message: options?.message || '',
          confirmText: options?.confirmText || 'Ok',
          cancelText: options?.cancelText || 'Cancel',
          showCancel: Boolean(options?.showCancel),
        });
      }),
    []
  );

  const showAlert = useCallback(async (type, message) => {
    const variant = type === 'error' ? 'error' : type === 'warning' ? 'info' : 'info';

    await alert({
      type: 'alert',
      variant,
      icon: 'alert',
      title:
        type === 'error'
          ? t?.alert?.errorTitle || 'Error'
          : type === 'warning'
            ? t?.alert?.warningTitle || 'Warning'
            : t?.alert?.infoTitle || 'Info',
      message: message || '',
      confirmText: 'Ok',
    });
  }, [alert, t?.alert?.errorTitle, t?.alert?.infoTitle, t?.alert?.warningTitle]);

  const fetchAutomations = useCallback(async (organizationId) => {
    setLoading(true);

    try {
      if (!organizationId) {
        setRows([]);
        setRowCount(0);
        return;
      }

      const activeSort = sortModel && sortModel.length > 0 ? sortModel[0] : { field: 'id', sort: 'desc' };
      const response = await axios.get(apiUrl(`automation/filtered/${organizationId}`), {
        params: {
          page,
          pageSize,
          sortField: activeSort.field || 'id',
          sortOrder: activeSort.sort || 'desc',
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });
      const nextRows = Array.isArray(response.data?.data) ? response.data.data : [];
      setRows(nextRows);
      setRowCount(response.data?.pagination?.total ?? nextRows.length);

      if (!Array.isArray(response.data?.data)) {
        await showAlert('error', 'Received an invalid format from the server.');
      }
    } catch (error) {
      setRows([]);
      setRowCount(0);
      await showAlert('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filterModel, page, pageSize, showAlert, sortModel]);

  useEffect(() => {
    setRefresh(false);

    if (org?.id) {
      fetchAutomations(org.id);
      return;
    }

    setRowCount(0);
    setLoading(false);
  }, [fetchAutomations, org?.id, refresh]);

  const canDeleteAutomations = Boolean(permissions?.delete_automations);
  const canCreateAutomations = Boolean(permissions?.create_automations);
  const canEditAutomations = Boolean(permissions?.edit_automations);
  const canTransferAutomations = canEditAutomations;
  const canViewAutomations = Boolean(permissions?.view_automations);

  const columns = useMemo(
    () => [
      ...(canDeleteAutomations || canEditAutomations
        ? [
          {
            field: 'actions',
            headerName: t.column.actions,
            type: 'actions',
            width: 100,
            getActions: (params) => {
              const actions = [];

              if (canTransferAutomations) {
                actions.push(
                  <GridActionsCellItem
                    key="transfer"
                    icon={<SyncAltOutlinedIcon />}
                    label={t?.transferOrganization?.title || 'Transfer Automation'}
                    onClick={() => setTransfer({ open: true, id: params.row.id })}
                    showInMenu
                  />
                );
              }

              if (canDeleteAutomations) {
                actions.push(
                  <GridActionsCellItem
                    key="delete"
                    icon={<DeleteIcon className="auto-icon-danger" />}
                    label={t.delete}
                    onClick={() => deleteAutomation(params.row.id, () => setRefresh(true), alert)}
                    showInMenu
                  />
                );
              }

              if (canEditAutomations && params.row.enabled) {
                actions.push(
                  <GridActionsCellItem
                    key="disable"
                    icon={<DisableIcon className="auto-icon-danger" />}
                    label={t.disable}
                    onClick={() =>
                      setAutomationEnabled(
                        [params.row.id],
                        false,
                        () => setRefresh(true),
                        alert
                      )
                    }
                    showInMenu
                  />
                );
              }

              if (canEditAutomations && !params.row.enabled) {
                actions.push(
                  <GridActionsCellItem
                    key="enable"
                    icon={<EnableIcon className="auto-icon-danger" />}
                    label={t.enable}
                    onClick={() =>
                      setAutomationEnabled(
                        [params.row.id],
                        true,
                        () => setRefresh(true),
                        alert
                      )
                    }
                    showInMenu
                  />
                );
              }

              return actions;
            }
          },
        ]
        : []),
      { field: 'id', headerName: t.column.id, align: 'center', headerAlign: 'center', width: 100 },
      { field: 'organization_name', headerName: t.column.organization, align: 'center', headerAlign: 'center', width: 300 },
      { field: 'trigger', headerName: t.column.trigger, align: 'center', headerAlign: 'center', width: 150 },
      { field: 'device_name', headerName: t.column.device, align: 'center', headerAlign: 'center', width: 300 },
      { field: 'enabled', headerName: t.column.enabled, align: 'center', headerAlign: 'center', width: 150 },
      { field: 'description', headerName: t.column.description, align: 'center', headerAlign: 'center', width: 400 },

    ],
    [
      alert,
      canDeleteAutomations,
      canEditAutomations,
      canTransferAutomations,
      t,
    ]
  );

  const selectActions = useMemo(
    () => {
      const actions = [];

      if (canDeleteAutomations) {
        actions.push({
            title: t.delete,
            icon: <DeleteIcon className='auto-icon-danger auto-icon-danger--spaced' />,
            action: async (ids) => {
              await deleteMultiAutomation(ids, () => setRefresh(true), alert);
              setSelectedRows([]);
            },
          });
      }

      if (canEditAutomations) {
        actions.push(
          {
            title: t.disable,
            icon: <DisableIcon className='auto-icon-danger auto-icon-danger--spaced' />,
            action: async (ids) => {
              await setAutomationEnabled(ids, false, () => setRefresh(true), alert);
              setSelectedRows([]);
            },
          },
          {
            title: t.enable,
            icon: <EnableIcon className='auto-icon-danger auto-icon-danger--spaced' />,
            action: async (ids) => {
              await setAutomationEnabled(ids, true, () => setRefresh(true), alert);
              setSelectedRows([]);
            },
          },
        );
      }

      return actions;
    },
    [alert, canDeleteAutomations, canEditAutomations, t]
  );

  if (!canViewAutomations) {
    return <div>{t.noPermission}</div>;
  }

  return (
    <div className='configuration devices-page auto-page' dir={isRtl ? 'rtl' : 'ltr'}>
      <AutomationTransferOrganizationModal
        state={transfer}
        setState={setTransfer}
        refresh={() => setRefresh(true)}
      />

      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />

      <div className='device-main'>
        <div className='devices-header'>
          <div className='devices-header-left'>
            <span className='auto-header-icon'>
              <AutomationHeaderIcon />
            </span>
            <h2 className='devices-title'>{t.automationsPageTitle}</h2>
          </div>

          {canCreateAutomations ? (
            <button className='devices-primary-btn' onClick={() => navigate('/automations/new')}>
              <SettingsSuggestIcon />
              <span>{t.buttonNew}</span>
            </button>
          ) : null}
        </div>

        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className='devices-grid-wrapper auto-grid-wrapper'>
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn='id'
              pagingoptions={[10, 20, 50, 100]}
              handleRowClick={(params) => navigate(`/automations/details/${params.row.id}`)}
              checkboxSelection={canDeleteAutomations || canEditAutomations}
              rowSelectionModel={selectedRows}
              setRowSelectionModel={setSelectedRows}
              selectActions={selectActions}
              showToolbar={true}
              tableKey='automations'
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
            />
          </div>
        ) : (
          <div className='auto-no-data'>{t.noData}</div>
        )}
      </div>
    </div>
  );
};

export const NewAutomation = () => <AutomationBuilderPage mode='create' />;

export const AutomationDetails = () => {
  const { id } = useParams();
  return <AutomationBuilderPage mode='edit' automationId={id} />;
};
